"use client";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Lightweight, client-only "where was I?" memory. Tracks the last
 * N entities the operator opened across surfaces and the last
 * filter/queue href per surface, so:
 *
 *   - the global keyboard shortcut "g r" can offer a recent list
 *   - long-running detail pages can render a "Recent" strip
 *   - returning to a list after an action can land on the right
 *     filter even when the URL `?from=` chain was lost
 *
 * Storage:
 *   - localStorage; tenant-scoped key so switching orgs doesn't
 *     leak issue keys across tenants in the recent list.
 *   - No server-side persistence — recents are per-device on
 *     purpose. Server-side history lives in audit/event logs.
 *
 * Schema is versioned (`v1`) so a future shape change can no-op
 * old entries instead of crashing on parse.
 */

export type RecentEntityKind =
  | "issue"
  | "patch"
  | "inbox"
  | "run"
  | "follow-up"
  | "knowledge";

export type RecentEntity = {
  kind: RecentEntityKind;
  /** Stable id (issue key, patch id, inbox id, …). */
  id: string;
  /** Display label (e.g. "BIZ-12 — Pay.nl integratie"). */
  label: string;
  /** Internal href to open the entity. */
  href: string;
  /** ms since epoch — last opened. */
  openedAt: number;
};

export type RecentQueue = {
  /** Surface name ("inbox", "issues", "patches", …). */
  surface: string;
  /** Display label (often "Issues — Ready"). */
  label: string;
  /** Full href incl. search params (filters/page/sort/selected). */
  href: string;
  visitedAt: number;
};

type Store = {
  v: 1;
  entities: RecentEntity[];
  queues: RecentQueue[];
};

const KEY_PREFIX = "fixcontrol:workflow-memory";
const MAX_ENTITIES = 12;
const MAX_QUEUES = 8;

function storageKey(scope: string | undefined): string {
  return `${KEY_PREFIX}:${scope ?? "default"}`;
}

function readStore(scope: string | undefined): Store {
  if (typeof window === "undefined") return { v: 1, entities: [], queues: [] };
  try {
    const raw = window.localStorage.getItem(storageKey(scope));
    if (!raw) return { v: 1, entities: [], queues: [] };
    const parsed = JSON.parse(raw);
    if (parsed && parsed.v === 1 && Array.isArray(parsed.entities) && Array.isArray(parsed.queues)) {
      return parsed as Store;
    }
  } catch {
    // ignore parse errors; treat as empty
  }
  return { v: 1, entities: [], queues: [] };
}

function writeStore(scope: string | undefined, store: Store): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(scope), JSON.stringify(store));
  } catch {
    // quota or privacy mode — silently no-op; recents are best-effort.
  }
}

function dedupeEntities(list: RecentEntity[]): RecentEntity[] {
  const seen = new Map<string, RecentEntity>();
  for (const e of list) {
    seen.set(`${e.kind}:${e.id}`, e);
  }
  return Array.from(seen.values()).sort((a, b) => b.openedAt - a.openedAt);
}

function dedupeQueues(list: RecentQueue[]): RecentQueue[] {
  const seen = new Map<string, RecentQueue>();
  for (const q of list) {
    seen.set(q.surface, q);
  }
  return Array.from(seen.values()).sort((a, b) => b.visitedAt - a.visitedAt);
}

export type UseWorkflowMemoryOptions = {
  /** Tenant or workspace scope to namespace storage. */
  scope?: string;
};

export function useWorkflowMemory(opts: UseWorkflowMemoryOptions = {}) {
  const { scope } = opts;
  const [store, setStore] = useState<Store>(() => readStore(scope));

  // Re-hydrate when scope changes (e.g. tenant switch).
  useEffect(() => {
    setStore(readStore(scope));
  }, [scope]);

  // Prune recents whose entity no longer exists (deleted issue, removed
  // inbox row, …). localStorage never hears about deletions, so without
  // this the palette keeps offering dead links until they age out. Server
  // round-trip is throttled per scope; every hook instance re-reads after
  // the shared validation settles so all surfaces converge.
  useEffect(() => {
    let cancelled = false;
    void validateRecents(scope).then((changed) => {
      if (changed && !cancelled) setStore(readStore(scope));
    });
    const onPruned = () => {
      if (!cancelled) setStore(readStore(scope));
    };
    window.addEventListener(WORKFLOW_MEMORY_PRUNED_EVENT, onPruned);
    return () => {
      cancelled = true;
      window.removeEventListener(WORKFLOW_MEMORY_PRUNED_EVENT, onPruned);
    };
  }, [scope]);

  const persist = useCallback(
    (mut: (prev: Store) => Store) => {
      setStore((prev) => {
        const next = mut(prev);
        writeStore(scope, next);
        return next;
      });
    },
    [scope],
  );

  const recordEntity = useCallback(
    (entity: Omit<RecentEntity, "openedAt"> & { openedAt?: number }) => {
      persist((prev) => {
        const next: RecentEntity = {
          ...entity,
          openedAt: entity.openedAt ?? Date.now(),
        };
        const merged = dedupeEntities([next, ...prev.entities]).slice(0, MAX_ENTITIES);
        return { ...prev, entities: merged };
      });
    },
    [persist],
  );

  const recordQueue = useCallback(
    (queue: Omit<RecentQueue, "visitedAt"> & { visitedAt?: number }) => {
      persist((prev) => {
        const next: RecentQueue = {
          ...queue,
          visitedAt: queue.visitedAt ?? Date.now(),
        };
        const merged = dedupeQueues([next, ...prev.queues]).slice(0, MAX_QUEUES);
        return { ...prev, queues: merged };
      });
    },
    [persist],
  );

  const clear = useCallback(() => {
    persist(() => ({ v: 1, entities: [], queues: [] }));
  }, [persist]);

  return useMemo(
    () => ({
      entities: store.entities,
      queues: store.queues,
      recordEntity,
      recordQueue,
      clear,
    }),
    [store, recordEntity, recordQueue, clear],
  );
}

/* ── Server-backed pruning of deleted entities ───────────────────────
 *
 * POST /api/recents/validate answers which remembered {kind, id} pairs
 * no longer resolve (deleted, or moved out of the caller's read scope).
 * Validation is best-effort: on any fetch failure we keep everything.
 * Throttled per scope and deduped in flight so the palette, the recent
 * strip, and the recorder hooks don't stampede the endpoint.
 */

const VALIDATE_TTL_MS = 60_000;
const lastValidatedAt = new Map<string, number>();
const inflightValidation = new Map<string, Promise<boolean>>();

/** Fired on window after a prune rewrites the store, so every mounted
 *  hook instance (palette, recent strip) re-reads the same storage. */
export const WORKFLOW_MEMORY_PRUNED_EVENT = "fixcontrol:workflow-memory-pruned";

/** Validate + prune the stored recents for a scope. Resolves true when
 *  the store changed. Exported for tests; UI goes through the hook. */
export function validateRecents(scope: string | undefined): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  const key = storageKey(scope);
  const inflight = inflightValidation.get(key);
  if (inflight) return inflight;
  const last = lastValidatedAt.get(key) ?? 0;
  if (Date.now() - last < VALIDATE_TTL_MS) return Promise.resolve(false);

  const entities = readStore(scope).entities;
  if (entities.length === 0) {
    lastValidatedAt.set(key, Date.now());
    return Promise.resolve(false);
  }

  const run = (async (): Promise<boolean> => {
    try {
      const res = await fetch("/api/recents/validate", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          items: entities.map((e) => ({ kind: e.kind, id: e.id })),
        }),
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { missing?: string[] };
      const missing = new Set(data.missing ?? []);
      if (missing.size === 0) return false;
      // Re-read at write time: an entity recorded while the request was
      // in flight must survive the prune.
      const store = readStore(scope);
      const kept = store.entities.filter((e) => !missing.has(`${e.kind}:${e.id}`));
      if (kept.length === store.entities.length) return false;
      writeStore(scope, { ...store, entities: kept });
      window.dispatchEvent(new Event(WORKFLOW_MEMORY_PRUNED_EVENT));
      return true;
    } catch {
      return false; // network hiccup — recents are best-effort
    } finally {
      lastValidatedAt.set(key, Date.now());
      inflightValidation.delete(key);
    }
  })();
  inflightValidation.set(key, run);
  return run;
}

/**
 * Imperative API for code paths that don't have access to the
 * hook (e.g. global navigation events). Always safe to call from
 * the client.
 */
export function recordRecentEntity(scope: string | undefined, entity: Omit<RecentEntity, "openedAt">): void {
  const store = readStore(scope);
  const merged = dedupeEntities([{ ...entity, openedAt: Date.now() }, ...store.entities]).slice(
    0,
    MAX_ENTITIES,
  );
  writeStore(scope, { ...store, entities: merged });
}

export function recordRecentQueue(scope: string | undefined, queue: Omit<RecentQueue, "visitedAt">): void {
  const store = readStore(scope);
  const merged = dedupeQueues([{ ...queue, visitedAt: Date.now() }, ...store.queues]).slice(0, MAX_QUEUES);
  writeStore(scope, { ...store, queues: merged });
}
